import { RadioGroupComponent, STEP_TYPE } from '@abiglobalhealth/go-types'
import { RadioOption } from '@components/RadioGroup/RadioOption'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useChoices } from '@/hooks/useChoices'
import { useFormErrors } from '@/hooks/useFormErrors'
import { useModule } from '@/hooks/useModule'
import { useStep } from '@/hooks/useStep'
import { useUserForm } from '@/hooks/useUserForm'

export type RadioGroupProps = Omit<RadioGroupComponent, 'componentType'> & {
  tabIndex: number
}

export const RadioGroup = ({ id, textKey, options, required, tabIndex }: RadioGroupProps) => {
  const { t } = useTranslation()

  const { addData, getUserDataById } = useUserForm()
  const { currentStep } = useStep()
  const { currentModule } = useModule()
  const { addChoice, choices } = useChoices()
  const { clearFormError, getFieldError } = useFormErrors()

  const initialSelected = useMemo(
    () =>
      options.find((option) => option.value === getUserDataById(id))?.id ||
      choices.find((choice) => choice.stepId === currentStep?.id && choice.component.id === id)?.options?.find((option) => option.optionId)
        ?.optionId ||
      null,
    []
  )

  const [selected, setSelected] = useState<string | null>(null)

  useEffect(() => {
    if (!selected) return

    switch (currentStep?.type) {
      case STEP_TYPE.USER_FORM:
        clearFormError(id)
        addData(id, options.find((option) => option.id === selected)?.value || '')
        break

      case STEP_TYPE.CHOICES: {
        if (!currentModule?.id) throw new Error('Module id is undefined')

        const selectedOption = options.find((option) => option.id === selected)
        if (!selected || !selectedOption) throw new Error('No selected option found')

        addChoice({
          date: new Date(),
          component: {
            id,
            key: textKey as string,
            text: t(textKey as string)
          },
          stepId: currentStep.id,
          moduleId: currentModule.id,
          options: [
            {
              optionId: selectedOption.id,
              value: {
                key: selectedOption.value,
                text: t(selectedOption.textKey)
              }
            }
          ]
        })

        break
      }
    }
  }, [selected])

  const error = getFieldError(id)

  return (
    <div id={id} data-testid={`${id}-container`} className="flex flex-col gap-3">
      {textKey && (
        <label className="font-semibold">
          {t(textKey)} {required && <span> *</span>}
        </label>
      )}

      {options.map((option, index) => {
        const elementTabIndex = Math.abs(tabIndex - options.length + index) + 1
        return (
          <RadioOption
            key={option.id}
            selected={selected}
            setSelected={setSelected}
            tabIndex={elementTabIndex}
            initialSelected={initialSelected}
            groupError={error}
            {...option}
          />
        )
      })}

      <span className="text-error min-h-[1.5rem] text-sm" data-testid={`${id}-error`}>
        {error && t(error)}
      </span>
    </div>
  )
}
